/**
 * Input sanitization utilities for XSS prevention
 * Sanitizes user input to prevent cross-site scripting attacks
 */

/**
 * Sanitize text input by removing potentially dangerous characters
 * @param input - Raw user input string
 * @returns Sanitized string safe for storage and display
 */
export function sanitizeText(input: string): string {
  if (!input) return '';
  
  return input
    .replace(/[<>]/g, '') // Remove angle brackets
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, '') // Remove event handlers like onclick=
    .trim();
}

/**
 * Sanitize email input
 * @param email - Raw email input
 * @returns Sanitized email string
 */
export function sanitizeEmail(email: string): string {
  if (!email) return '';
  
  return email
    .toLowerCase()
    .replace(/[<>"']/g, '')
    .trim();
}

/**
 * Sanitize numeric input
 * @param input - Raw numeric input
 * @returns Sanitized number string
 */
export function sanitizeNumber(input: string): string {
  if (!input) return '';
  
  return input.replace(/[^\d.]/g, '').trim();
}

/**
 * Sanitize phone number input
 * @param phone - Raw phone input
 * @returns Sanitized phone string
 */
export function sanitizePhone(phone: string): string {
  if (!phone) return '';
  
  return phone.replace(/[^\d+\s()-]/g, '').trim();
}

/**
 * Sanitize alphanumeric input (for IDs, registration numbers)
 * @param input - Raw alphanumeric input
 * @returns Sanitized alphanumeric string
 */
export function sanitizeAlphanumeric(input: string): string {
  if (!input) return '';
  
  return input.replace(/[^a-zA-Z0-9\s-]/g, '').trim();
}

/**
 * Escape HTML entities to prevent XSS
 * @param text - Raw text that may contain HTML
 * @returns HTML-escaped string
 */
export function escapeHtml(text: string): string {
  if (!text) return '';
  
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };
  
  return text.replace(/[&<>"'/]/g, (char) => map[char] || char);
}

/**
 * Validate and sanitize file name
 * @param fileName - Original file name
 * @returns Safe file name
 */
export function sanitizeFileName(fileName: string): string {
  if (!fileName) return '';
  
  return fileName
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/\.{2,}/g, '.')
    .substring(0, 255);
}